import React, { useCallback, useEffect } from 'react';
import Image from 'next/image';
import { Box, Typography, Modal as MUIModal, IconButton } from '@mui/material';
import QRCode from 'react-qr-code';
import { trackEvent } from '@surfline/web-common';
import config from 'config';
import { CloseIcon } from 'components/Icons/CloseIcon';
import WavetrakLink from 'components/WavetrakLink';
import usePageName from 'hooks/usePageName';
import styles from './GetAppModal.module.scss';

// Official badge images downloaded from:
// Google Play: https://play.google.com/intl/en_us/badges/
// Apple App Store: https://developer.apple.com/app-store/marketing/guidelines/
// @ts-expect-error Image can't be found on this path
import googlePlayBadge from '../../../public/images/google-play-badge.png';
// @ts-expect-error Image can't be found on this path
import appStoreBadge from '../../../public/images/app-store-badge.svg';

interface GetAppModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

const GetAppModal: React.FC<GetAppModalProps> = ({ open, setOpen }) => {
  const qrCodeUrl = config.getAppUrl;
  const pageName = usePageName();

  useEffect(() => {
    if (open) {
      trackEvent('Mobile CTA Viewed', {
        ctaType: 'qr_code',
        pageScreenName: pageName,
      });
    }
  }, [open, pageName]);

  const handleGooglePlayClick = useCallback(() => {
    trackEvent('Button Clicked', {
      uiElement: 'web_to_app_qr_code',
      button_text: 'google_play',
      pageName,
    });
  }, [pageName]);

  const handleAppStoreClick = useCallback(() => {
    trackEvent('Button Clicked', {
      uiElement: 'web_to_app_qr_code',
      button_text: 'app_store',
      pageName,
    });
  }, [pageName]);

  const handleClose = () => setOpen(false);

  return (
    <MUIModal open={open} onClose={handleClose}>
      <div className={styles.modalContent}>
        <div className={styles.contentWrapper}>
          <IconButton className={styles.closeButton} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
          <Box className={styles.content}>
            <Typography variant="h4" component="h2" className={styles.title} color="text.primary">
              Get the Surfline App
            </Typography>
            <Typography
              variant="body"
              component="p"
              className={styles.description}
              color="text.primary"
            >
              Scan the QR code with your phone to download the app
            </Typography>
            <Box className={styles.qrCodeContainer}>
              <Box className={styles.qrCodeWrapper}>
                <QRCode value={qrCodeUrl} size={250} />
              </Box>
            </Box>
            <Typography
              variant="footnote"
              component="p"
              className={styles.checkOutAppStoresText}
              color="text.secondary"
            >
              Or check it out in the app stores
            </Typography>
            <Box className={styles.storeButtons}>
              <WavetrakLink
                href={config.appStoreUrl.android}
                isExternal
                onClick={handleGooglePlayClick}
              >
                <Box className={styles.storeBadge}>
                  <Image
                    src={googlePlayBadge}
                    alt="Get it on Google Play"
                    width={155}
                    height={60}
                    className={styles.badgeImage}
                    unoptimized
                  />
                </Box>
              </WavetrakLink>
              <WavetrakLink href={config.appStoreUrl.iOS} isExternal onClick={handleAppStoreClick}>
                <Box className={`${styles.storeBadge} ${styles.appStoreBadge}`}>
                  <Image
                    src={appStoreBadge}
                    alt="Download on the App Store"
                    width={155}
                    height={60}
                    className={styles.badgeImage}
                    unoptimized
                  />
                </Box>
              </WavetrakLink>
            </Box>
          </Box>
        </div>
      </div>
    </MUIModal>
  );
};

export default GetAppModal;
